/**
 * Submit handling for the public forms (contact, license request).
 *
 * The reCAPTCHA v3 token is minted here, on submit, and never on page load. A v3
 * token expires after two minutes and is single use, so a token created when the
 * page loaded is already dead by the time anyone finishes writing a message, and
 * the server then had no score to judge.
 *
 * If Google is slow, blocked by an extension, or fails outright we still submit,
 * just without a token. The server treats a missing token as unverified rather
 * than as spam, so the message is delivered and flagged instead of discarded.
 */

declare const grecaptcha: {
    ready(callback: () => void): void;
    execute(siteKey: string, options: { action: string }): Promise<string>;
} | undefined;

const SITE_KEY = "6LeDUPEZAAAAAD0gd2vFUn6VfUAHRoCBGLKAYpsJ";
const TOKEN_TIMEOUT_MS = 5000;
const FORM_TOKEN_URL = "/form_token.php";
const FORM_TOKEN_TIMEOUT_MS = 3000;

/**
 * Asks our own server for a token proving this page was really rendered.
 *
 * Same origin and a few bytes, so it is far more reliable than reaching Google.
 * A bot that posts straight to contact.php never gets one, which is what lets the
 * server tell a real visitor apart from a script. If the request fails we submit
 * without a token and the server falls back to judging the message content.
 */
function requestFormToken(): Promise<string> {
    return new Promise<string>((resolve) => {
        // The timeout resolves on its own rather than aborting the request, so a
        // fetch that never settles cannot hold the submission hostage.
        const giveUp = window.setTimeout(() => resolve(""), FORM_TOKEN_TIMEOUT_MS);
        const finish = (token: string) => {
            window.clearTimeout(giveUp);
            resolve(token);
        };

        try {
            fetch(FORM_TOKEN_URL, { cache: "no-store" })
                .then((response) => (response.ok ? response.json() : null))
                .then((data) => finish(data && typeof data.token === "string" ? data.token : ""))
                .catch(() => finish(""));
        } catch {
            finish("");
        }
    });
}

function requestToken(action: string): Promise<string> {
    return new Promise<string>((resolve) => {
        const giveUp = window.setTimeout(() => resolve(""), TOKEN_TIMEOUT_MS);
        const finish = (token: string) => {
            window.clearTimeout(giveUp);
            resolve(token);
        };

        if (typeof grecaptcha === "undefined" || !grecaptcha) {
            finish("");
            return;
        }

        try {
            grecaptcha.ready(() => {
                grecaptcha!.execute(SITE_KEY, { action })
                    .then(finish)
                    .catch(() => finish(""));
            });
        } catch {
            finish("");
        }
    });
}

function setHiddenValue(form: HTMLFormElement, name: string, value: string): void {
    let field = form.querySelector<HTMLInputElement>(`input[name="${name}"]`);

    if (!field) {
        field = document.createElement("input");
        field.type = "hidden";
        field.name = name;
        form.appendChild(field);
    }

    field.value = value;
}

function setupForm(form: HTMLFormElement, action: string): void {
    const loadedAt = Date.now();

    form.addEventListener("submit", (event) => {
        event.preventDefault();

        if (!form.reportValidity()) {
            return;
        }

        const button = form.querySelector<HTMLButtonElement>("button[type=submit]");
        if (button) {
            button.disabled = true;
        }

        // How long the visitor spent on the form. The server rejects implausibly
        // fast submissions, which no human produces but scripted ones do.
        setHiddenValue(form, "c-elapsed", String(Date.now() - loadedAt));
        // Without a trailing slash: the hosting also serves pages like
        // /aktualizacia-licencie/, and the visitor should land back on the canonical URL.
        setHiddenValue(form, "c-return", window.location.pathname.replace(/\/+$/, "") || "/");

        // The catch is the safety net that matters: whatever goes wrong while
        // collecting tokens, the message still gets submitted.
        Promise.all([requestToken(action), requestFormToken()])
            .catch(() => ["", ""])
            .then(([captchaToken, formToken]) => {
                const tokenField = form.querySelector<HTMLInputElement>("[data-recaptcha-token]");
                if (tokenField) {
                    tokenField.value = captchaToken;
                }

                // Deliberately not in the markup: a bot that scrapes the form
                // cannot learn the field exists, let alone produce a valid value.
                setHiddenValue(form, "c-form-token", formToken);

                // form.submit() does not fire the submit event, so this cannot loop.
                form.submit();
            });
    });
}

function init(): void {
    const forms = document.querySelectorAll<HTMLFormElement>("form[data-form-action]");
    forms.forEach((form) => setupForm(form, form.dataset.formAction || "submit"));
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
} else {
    init();
}
